<script setup>
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { StealieButton, StealieParagraph } from "@get-overalls/fe-stealie";

defineProps({
  user: {
    type: Object,
    required: true,
  },
  company: {
    type: Object,
    required: true,
  },
});

const isOpen = defineModel("open", { required: true, default: false, type: Boolean });
defineEmits(["close"]);

function handleClose() {
  isOpen.value = false;
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-20 bg-black/25 sm:bg-transparent"
        @click="handleClose"
      ></div>
    </Transition>
    <Transition
      enter-active-class="transition-all ease-out duration-100"
      enter-from-class="opacity-0 not-sm:-translate-y-full"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 not-sm:-translate-y-full"
    >
      <div
        v-if="isOpen"
        class="fixed top-0 right-0 left-0 z-20 bg-white shadow-sm sm:top-2 sm:right-3 sm:left-auto sm:w-sm sm:rounded-lg sm:border sm:border-brand-gray-100/60 sm:shadow-md"
      >
        <button
          class="absolute top-4 right-5.75 z-20 flex size-8 cursor-pointer items-center justify-center rounded-full bg-transparent text-gray-500 transition duration-300 hover:bg-gray-500/8 hover:text-black focus-visible:ring focus-visible:ring-focus-highlight-light focus-visible:outline-none sm:top-2 sm:right-2"
          @click="handleClose"
        >
          <XMarkIcon class="size-5" aria-hidden="true" />
          <span class="sr-only">close</span>
        </button>
        <div class="w-full p-8">
          <div class="mt-6 text-center" data-sr-mask>
            <h1 class="truncate text-2xl font-semibold text-black">
              {{ user.fullName }}
            </h1>
            <h2 class="mt-0.5 text-base text-brand-gray-400">
              {{ user.email }}
            </h2>
          </div>
          <div class="mt-6 flex flex-col items-center justify-center space-y-3">
            <StealieButton
              variant="tertiary"
              :to="{ name: 'Account' }"
              data-sr-unmask
              @click="handleClose"
            >
              Manage your account
            </StealieButton>
          </div>
          <footer
            v-if="company.name"
            class="mt-7 flex flex-col items-center gap-y-3 border-t border-brand-gray-50 px-2 py-4"
            data-sr-unmask
          >
            <StealieParagraph
              class="text-center text-xs text-balance text-brand-gray-400"
            >
              Your access to Overalls is provided by:
            </StealieParagraph>
            <img v-if="company.logoUrl" :src="company.logoUrl" :alt="company.name" />
            <StealieParagraph
              v-else
              class="text-center text-xl font-semibold text-balance text-brand-gray-500"
            >
              {{ company.name }}
            </StealieParagraph>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
